import type { CustomerRow } from './customers';
import { buildWorkbook, XLSX_MIME_TYPE, type Sheet, type SheetColumn } from './xlsx';

/**
 * The Excel export. It receives exactly the rows the list is showing, in
 * the order they are shown, so what is exported is what was filtered and
 * sorted on screen. Every value is written as text: ZIP codes such as
 * "02134", route numbers such as "0415" and phone numbers keep their
 * formatting. A second sheet records what the export covered.
 */

export const CUSTOMER_EXPORT_COLUMNS: readonly SheetColumn[] = [
  { header: 'Distributor', width: 24 },
  { header: 'Route', width: 9 },
  { header: 'Customer', width: 34 },
  { header: 'Street address', width: 32 },
  { header: 'City', width: 18 },
  { header: 'State', width: 7 },
  { header: 'ZIP', width: 11 },
  { header: 'Contact', width: 22 },
  { header: 'Phone', width: 15 },
  { header: 'Other phone', width: 15 },
];

export function customerExportRow(row: CustomerRow): string[] {
  const { stop } = row;
  return [
    row.distributorName,
    row.routeNumber,
    stop.name,
    stop.streetAddress ?? '',
    stop.city ?? '',
    stop.state ?? '',
    stop.zipCode ?? '',
    stop.contactName ?? '',
    stop.primaryPhone ?? '',
    stop.secondaryPhone ?? '',
  ];
}

export interface CustomerExportOptions {
  rows: readonly CustomerRow[];
  /** What the list covered, e.g. "All permitted routes" or "Route 0415 · Sunny Grove". */
  scope: string;
  account: string;
  search: string;
  sort: string;
  exportedAt?: Date;
}

export function buildCustomerWorkbook(options: CustomerExportOptions): Uint8Array<ArrayBuffer> {
  const exportedAt = options.exportedAt ?? new Date();
  const customers: Sheet = {
    name: 'Customers',
    columns: CUSTOMER_EXPORT_COLUMNS,
    rows: options.rows.map(customerExportRow),
    freezeHeader: true,
    autoFilter: true,
  };
  const details: Sheet = {
    name: 'Export details',
    columns: [
      { header: 'Item', width: 16 },
      { header: 'Value', width: 80 },
    ],
    rows: [
      ['Exported', exportedAt.toLocaleString('en-US')],
      ['Account', options.account],
      ['Covers', options.scope],
      ['Search', options.search || '(none)'],
      ['Sort', options.sort],
      ['Customers', String(options.rows.length)],
      [
        'Note',
        'Every column is text so ZIP codes, route numbers and phone numbers keep their formatting. Routes are listed with their distributor because the same route number can exist at more than one distributor.',
      ],
    ],
  };
  return buildWorkbook([customers, details], exportedAt);
}

/** "Route 0415 · Sunny Grove" on 28 Sep 2026 → "route-0415-sunny-grove-2026-09-28.xlsx". */
export function exportFileName(prefix: string, date = new Date()): string {
  const safe = prefix
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  const pad = (value: number) => String(value).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${safe || 'customers'}-${day}.xlsx`;
}

/** Browser only: hands the workbook to the person as a file download. */
export function downloadWorkbook(bytes: Uint8Array<ArrayBuffer>, fileName: string): void {
  const url = URL.createObjectURL(new Blob([bytes], { type: XLSX_MIME_TYPE }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
